import { Card, CardContent } from "@/components/ui/card";
import { OverviewCardHeader } from "@/lib/dashboard/OverviewCardHeader";
import { BigNumber } from "@/components/ui/big-number";
import { OverviewCardRow } from "@/lib/dashboard/OverviewCardRow";
import { useI18n } from "@/hooks/useI18n";

interface ClientsAndTripsCardProps {
  clientsCount: number;
  activeCount: number;
  completedCount: number;
  /** Conteggi di piattaforma: valorizzati solo per admin, `null` per tutti gli altri. */
  agenciesCount: number | null;
  advisorsCount: number | null;
}

/**
 * Quante persone e quante pratiche: la card dei conteggi.
 *
 * I numeri grandi sono due -- clienti e pratiche -- perche' sono la coppia che si guarda
 * per prima aprendo una dashboard di agenzia: con chi lavoro, e su quanto. In corso e
 * completate restano righe sotto, perche' sono la scomposizione di "pratiche" e non due
 * totali indipendenti.
 *
 * All'amministrazione se ne aggiungono altri due, agenzie e travel advisor, sulla stessa
 * griglia: sono conteggi dello stesso tipo, e metterli altrove avrebbe voluto dire una
 * card in piu' che esiste per un ruolo solo. Il controllo e' sul `null` e non sul ruolo:
 * cosi' la regola di visibilita' vive in un posto solo, il database.
 */
export function ClientsAndTripsCard({
  clientsCount,
  activeCount,
  completedCount,
  agenciesCount,
  advisorsCount,
}: ClientsAndTripsCardProps) {
  const { t } = useI18n();

  return (
    <Card className="h-full">
      <CardContent className="p-4 h-full flex flex-col gap-4">
        <OverviewCardHeader
          title={t("dashboard.overview.clientsAndTrips.title")}
          info={t("dashboard.overview.clientsAndTrips.info")}
          to="/overview?tab=clients"
        />

        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
          <BigNumber
            value={String(clientsCount)}
            label={t("dashboard.overview.clientsAndTrips.clients")}
            info={t("dashboard.overview.clientsAndTrips.clientsInfo")}
          />
          {/* Le pratiche del perimetro sono la somma delle due righe qui sotto: il numero
              grande e' l'unico posto in cui compare il totale, le righe lo spiegano. */}
          <BigNumber
            value={String(activeCount + completedCount)}
            label={t("dashboard.overview.clientsAndTrips.trips")}
            info={t("dashboard.overview.clientsAndTrips.tripsInfo")}
          />

          {agenciesCount != null && (
            <BigNumber
              value={String(agenciesCount)}
              label={t("dashboard.overview.clientsAndTrips.agencies")}
              info={t("dashboard.overview.clientsAndTrips.agenciesInfo")}
            />
          )}
          {advisorsCount != null && (
            <BigNumber
              value={String(advisorsCount)}
              label={t("dashboard.overview.clientsAndTrips.advisors")}
              info={t("dashboard.overview.clientsAndTrips.advisorsInfo")}
            />
          )}
        </div>

        <div className="flex flex-col gap-1.5 mt-auto pt-3 border-t border-gray-100">
          <OverviewCardRow
            label={t("dashboard.overview.clientsAndTrips.tripsActive")}
            value={String(activeCount)}
            info={t("dashboard.overview.clientsAndTrips.tripsActiveInfo")}
          />
          <OverviewCardRow
            label={t("dashboard.overview.clientsAndTrips.tripsCompleted")}
            value={String(completedCount)}
            info={t("dashboard.overview.clientsAndTrips.tripsCompletedInfo")}
          />
        </div>
      </CardContent>
    </Card>
  );
}

export default ClientsAndTripsCard;
